import { Directive, Injector, OnInit, inject } from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * @summary The `NgControlRef` directive provides a reference to the `NgControl` instance in the current
 * component and initializes it during the `OnInit` lifecycle hook.
 * This directive ensures a consistent way of handling `NgControl` injection in a component constructor, maintaining the correct order of
 * execution between `writeValue` and the `ngOnInit` lifecycle hook.
 * By resolving the cyclic dependency issue, it eliminates the need for manually setting the value accessor `this.ngControl.valueAccessor = this`,
 * thus addressing inconsistencies in the behavior of `writeValue`, which is an Angular isssue For more details about this issue,
 * visit: https://github.com/angular/angular/issues/29218
 */

/**
 * This directive provides a reference to the `NgControl` instance in the current
 * component and initializes it during the `OnInit` lifecycle hook.
 * @example
 * ```
 * // In the component, the NgControlRef can be used like this:
 * @Component({ hostDirectives: [NgControlRef] })
 * export class ComponentA {
 *   constructor(private ngControlRef: NgControlRef) {}
 * }
 * ```
 */
@Directive({ standalone: true })
export class NgControlRef implements OnInit {
  /** The current instance of the `NgControl` class, which will be initialized during the `OnInit` lifecycle hook */
  current?: NgControl;

  private _injector = inject(Injector);

  ngOnInit(): void {
    this.current = this._injector.get(NgControl, null, {
      optional: true,
      self: true,
    });
  }
}

/** A helper function to inject an instance of the `NgControlRef` class. */
export function injectNgControlRef(): NgControlRef {
  return inject(NgControlRef);
}
